/** @jsx jsx */
import { jsx } from '@instructure/emotion';
import { BUILD_TRAY_OPEN, BUILD_TRAY_POSITION, BUILD_TRAY_STIMULUS_ID, withStyleOverrides } from '@instructure/quiz-common';
import t from '@instructure/quiz-i18n/format-message';
import { Button, IconButton } from '@instructure/ui-buttons';
import { Grid } from '@instructure/ui-grid';
import { IconAiSolid, IconBankLine } from '@instructure/ui-icons';
import { Text } from '@instructure/ui-text';
import { View } from '@instructure/ui-view';
import first from 'lodash/first';
import PropTypes from 'prop-types';
import React, { useCallback } from 'react';
import ImmutablePropTypes from 'react-immutable-proptypes';
import InteractionTypes from '../../../../shared/InteractionTypes';
import generateStyle from './styles';
import generateComponentTheme from './theme';
function UnstyledAddContentBody(_ref) {
  var allowStimulusCreation = _ref.allowStimulusCreation,
    createItem = _ref.createItem,
    createStimulus = _ref.createStimulus,
    editingQuizEntry = _ref.editingQuizEntry,
    interactionTypes = _ref.interactionTypes,
    _ref$onOpenBankTray = _ref.onOpenBankTray,
    onOpenBankTray = _ref$onOpenBankTray === void 0 ? function () {} : _ref$onOpenBankTray,
    _ref$onAddItem = _ref.onAddItem,
    onAddItem = _ref$onAddItem === void 0 ? function () {} : _ref$onAddItem,
    parentEntryId = _ref.parentEntryId,
    position = _ref.position,
    quizEntryWithErrorsShowing = _ref.quizEntryWithErrorsShowing,
    quizId = _ref.quizId,
    screenreaderNotification = _ref.screenreaderNotification,
    setNextQuizEntry = _ref.setNextQuizEntry,
    setUi = _ref.setUi,
    _ref$showHeaderText = _ref.showHeaderText,
    showHeaderText = _ref$showHeaderText === void 0 ? true : _ref$showHeaderText,
    stimulusId = _ref.stimulusId,
    styles = _ref.styles,
    _ref$slotProps = _ref.slotProps,
    slotProps = _ref$slotProps === void 0 ? {} : _ref$slotProps,
    _ref$containerType = _ref.containerType,
    containerType = _ref$containerType === void 0 ? 'popover' : _ref$containerType;
  var openBankTray = useCallback(function () {
    onOpenBankTray();
    setUi(BUILD_TRAY_OPEN, true);
    setUi(BUILD_TRAY_POSITION, position);
    setUi(BUILD_TRAY_STIMULUS_ID, parentEntryId);
  }, [onOpenBankTray, setUi, position, parentEntryId]);
  var addStimulus = useCallback(function (isPassage) {
    if (editingQuizEntry) {
      setNextQuizEntry(null, quizId, position);
    } else {
      createStimulus(quizId, position, isPassage);
    }
    onAddItem();
  }, [editingQuizEntry, setNextQuizEntry, quizId, position, createStimulus, onAddItem]);
  var addItem = useCallback(function (interactionTypeData) {
    if (quizEntryWithErrorsShowing) {
      screenreaderNotification(t('Question {itemPosition, number} cannot be saved due to invalid input data', {
        itemPosition: Math.ceil(quizEntryWithErrorsShowing.position)
      }));
    } else {
      if (editingQuizEntry) {
        setNextQuizEntry(interactionTypeData, quizId, position, stimulusId, parentEntryId);
      } else {
        var defaultInteractionData = interactionTypeData.getDefaultInteractionData();
        var scoringData = interactionTypeData.getDefaultScoringData(defaultInteractionData);
        createItem(quizId, position, {
          id: interactionTypeData.id,
          interactionData: defaultInteractionData,
          itemBody: interactionTypeData.itemBody,
          properties: interactionTypeData.defaultProperties,
          scoringAlgorithm: interactionTypeData.scoringAlgorithmDefault,
          scoringData: scoringData,
          userResponseType: first(interactionTypeData.userResponseTypeOptions)
        }, parentEntryId, stimulusId);
      }
    }
    onAddItem();
  }, [quizEntryWithErrorsShowing, screenreaderNotification, editingQuizEntry, setNextQuizEntry, quizId, position, stimulusId, parentEntryId, createItem, onAddItem]);
  var renderBankButton = function renderBankButton() {
    return jsx(IconButton, {
      size: "large",
      onClick: openBankTray,
      "data-automation": "sdk-add-banked-content-button",
      screenReaderLabel: t('Add banked content')
    }, jsx(IconBankLine, null));
  };
  var renderPopoverHeader = function renderPopoverHeader() {
    var _slotProps$quizGenera, _slotProps$quizGenera2, _slotProps$quizGenera3;
    var text = showHeaderText ? t('Insert Content') : '';
    return jsx("div", {
      css: styles.popoverHeaderBox
    }, jsx("div", {
      css: styles.popoverHeader
    }, jsx(Grid, {
      vAlign: "middle",
      colSpacing: "none",
      rowSpacing: "none"
    }, jsx(Grid.Row, null, jsx(Grid.Col, null, jsx(Text, {
      color: "primary"
    }, text)), jsx(Grid.Col, {
      width: "auto"
    }, renderBankButton())))), ((_slotProps$quizGenera = slotProps.quizGenerationButton) === null || _slotProps$quizGenera === void 0 ? void 0 : _slotProps$quizGenera.visible) && jsx(View, {
      as: "div",
      maxWidth: containerType === 'modal' ? '100%' : '50%',
      padding: containerType === 'modal' ? 'x-small 0 small' : 'x-small x-small small 0'
    }, jsx(Button, {
      size: "small",
      display: "block",
      color: "ai-primary",
      textAlign: "start",
      renderIcon: IconAiSolid,
      onClick: (_slotProps$quizGenera2 = slotProps.quizGenerationButton) === null || _slotProps$quizGenera2 === void 0 ? void 0 : _slotProps$quizGenera2.onClick,
      disabled: (_slotProps$quizGenera3 = slotProps.quizGenerationButton) === null || _slotProps$quizGenera3 === void 0 ? void 0 : _slotProps$quizGenera3.disabled,
      "data-automation": "sdk-interaction-button-ai"
    }, t('Generate with AI'))));
  };
  return jsx("div", {
    css: styles.popoverContent,
    role: "dialog",
    "aria-label": t('Insert Content')
  }, renderPopoverHeader(), jsx(View, {
    padding: "small"
  }, jsx(InteractionTypes, {
    columns: 2,
    addItem: addItem,
    addStimulus: addStimulus,
    allowStimulusCreation: allowStimulusCreation,
    types: interactionTypes
  })));
}
export var AddContentBody = withStyleOverrides(generateStyle, generateComponentTheme)(UnstyledAddContentBody);
export var quizGenerationButtonPropTypes = PropTypes.shape({
  onClick: PropTypes.func.isRequired,
  visible: PropTypes.bool,
  disabled: PropTypes.bool
});
AddContentBody.displayName = 'AddContentBody';
AddContentBody.componentId = "Quizzes".concat(AddContentBody.displayName);
AddContentBody.propTypes = {
  allowStimulusCreation: PropTypes.bool.isRequired,
  createItem: PropTypes.func.isRequired,
  createStimulus: PropTypes.func.isRequired,
  editingQuizEntry: PropTypes.bool,
  interactionTypes: ImmutablePropTypes.list,
  onOpenBankTray: PropTypes.func,
  onAddItem: PropTypes.func,
  parentEntryId: PropTypes.string,
  position: PropTypes.number.isRequired,
  quizEntryWithErrorsShowing: ImmutablePropTypes.record,
  quizId: PropTypes.string.isRequired,
  screenreaderNotification: PropTypes.func.isRequired,
  setNextQuizEntry: PropTypes.func.isRequired,
  setUi: PropTypes.func.isRequired,
  showHeaderText: PropTypes.bool,
  stimulusId: PropTypes.string,
  styles: PropTypes.object,
  slotProps: PropTypes.shape({
    quizGenerationButton: quizGenerationButtonPropTypes
  })
};
export default AddContentBody;